import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { GameService } from "../../game/game.service";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";
import {
  DEFAULT_SLIDER_SHORTCUT_BUTTONS_NL,
  DEFAULT_SLIDER_SHORTCUT_BUTTONS_PL,
} from "../../game/helpers/slider-shortcut-buttons.const";
import { MatDialog } from "@angular/material/dialog";
import { EnterCustomAmountDialogComponent } from "../enter-custom-amount-dialog/enter-custom-amount-dialog.component";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";

@Component({
  selector: "app-slider-shortcut-buttons",
  templateUrl: "./slider-shortcut-buttons.component.html",
  styleUrls: ["./slider-shortcut-buttons.component.scss"],
})
export class SliderShortcutButtonsComponent implements OnInit {
  form!: FormGroup;
  formNL!: FormGroup;
  formPL!: FormGroup;
  hasLimit: boolean = false;
  savedValue: string = "";
  isMobile: boolean = false;
  customPostflopValuesNL: { [key: string]: string | null } = {};
  customPostflopValuesPL: { [key: string]: string | null } = {};
  preflopOptions = [
    { value: "/", isDisabled: false },
    { value: "2BB", isDisabled: false },
    { value: "3BB", isDisabled: false },
    { value: "4BB", isDisabled: false },
    { value: "5BB", isDisabled: false },
    { value: "10BB", isDisabled: false },
    { value: "Pot", isDisabled: false },
    { value: "All-In", isDisabled: false },
  ];
  postflopOptions = [
    { value: "/", isDisabled: false },
    { value: "1/4 Pot", isDisabled: false },
    { value: "1/3 Pot", isDisabled: false },
    { value: "1/2 Pot", isDisabled: false },
    { value: "2/3 Pot", isDisabled: false },
    { value: "3/4 Pot", isDisabled: false },
    { value: "Pot", isDisabled: false },
    { value: "All-In", isDisabled: false },
    { value: "Enter %", isDisabled: false },
  ];

  constructor(
    private fb: FormBuilder,
    private gameService: GameService,
    private dialog: MatDialog,
    private _breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    this.formNL = this.fb.group({
      preflop: this.fb.group({
        btn1: ["2BB", Validators.required],
        btn2: ["3BB", Validators.required],
        btn3: ["4BB", Validators.required],
        btn4: ["All-In", Validators.required],
      }),
      postflop: this.fb.group({
        btn1: ["1/3 Pot", Validators.required],
        btn2: ["1/2 Pot", Validators.required],
        btn3: ["2/3 Pot", Validators.required],
        btn4: ["All-In", Validators.required],
      }),
    });

    this.formPL = this.fb.group({
      preflop: this.fb.group({
        btn1: ["2BB", Validators.required],
        btn2: ["3BB", Validators.required],
        btn3: ["4BB", Validators.required],
        btn4: ["Pot", Validators.required], // PL nema All-In
      }),
      postflop: this.fb.group({
        btn1: ["1/3 Pot", Validators.required],
        btn2: ["1/2 Pot", Validators.required],
        btn3: ["2/3 Pot", Validators.required],
        btn4: ["Pot", Validators.required],
      }),
    });

    this._breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(
        map((result) => result.matches),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
        if (!isMobile) {
          this.loadForm();
        } else {
          this.loadMobileData();
        }
      });
    this.loadForm();
  }

  loadMobileData() {
    this.form = this.hasLimit ? this.formPL : this.formNL;
    const postIndex = this.postflopOptions.findIndex(
      (option) =>
        option.value === this.form.get("postflop")?.get("btn4")?.value,
    );
    this.postflopOptions[postIndex].isDisabled = false;
    const preIndex = this.preflopOptions.findIndex(
      (option) => option.value === this.form.get("preflop")?.get("btn4")?.value,
    );
    this.preflopOptions[preIndex].isDisabled = false;
    this.form.patchValue({
      preflop: { btn4: "" },
      postflop: { btn4: "" },
    });
  }

  loadForm() {
    this.form = this.hasLimit ? this.formPL : this.formNL;
    const stored = localStorage.getItem(
      this.hasLimit ? "sliderShortCutOptionsPL" : "sliderShortCutOptionsNL",
    );

    if (stored) {
      const parsed = JSON.parse(stored);
      Object.entries(parsed.postflop).forEach(([btnKey, value]) => {
        if (typeof value === "string" && value.includes("%")) {
          if (this.hasLimit) this.customPostflopValuesPL[btnKey] = value;
          else this.customPostflopValuesNL[btnKey] = value;
        }
        if (value === "" && !this.isMobile) {
          const newValue = this.hasLimit
            ? this.postflopOptions[6].isDisabled
              ? null
              : this.postflopOptions[6].value
            : this.postflopOptions[7].isDisabled
              ? null
              : this.postflopOptions[7].value;
          parsed.postflop[btnKey] = newValue ?? this.postflopOptions[0].value;
        }
      });
      Object.entries(parsed.preflop).forEach(([btnKey, value]) => {
        if (value === "" && !this.isMobile) {
          const newValuePreFlop = this.hasLimit
            ? this.preflopOptions[6].isDisabled
              ? null
              : this.preflopOptions[6].value
            : this.preflopOptions[7].isDisabled
              ? null
              : this.preflopOptions[7].value;
          parsed.preflop[btnKey] =
            newValuePreFlop ?? this.preflopOptions[0].value;
        }
      });
      this.form.patchValue(parsed);
    }
    this.filterOptions();
    this.addToStorage();
    this.filterOptions(true);
  }

  filterOptions(postFlop = false) {
    const formGroup = this.form.get(
      postFlop ? "postflop" : "preflop",
    ) as FormGroup;
    if (postFlop) {
      this.postflopOptions.forEach((item) => {
        item.isDisabled =
          item.value === formGroup.get("btn1").value ||
          item.value === formGroup.get("btn2").value ||
          item.value === formGroup.get("btn3").value ||
          item.value === formGroup.get("btn4").value;
      });
    } else {
      this.preflopOptions.forEach((item) => {
        item.isDisabled =
          item.value === formGroup.get("btn1").value ||
          item.value === formGroup.get("btn2").value ||
          item.value === formGroup.get("btn3").value ||
          item.value === formGroup.get("btn4").value;
      });
    }
  }

  setDisabled(controlName: string, postFlop = false) {
    const formGroup = this.form.get(
      postFlop ? "postflop" : "preflop",
    ) as FormGroup;
    const controlItem = formGroup.get(controlName)?.value;
    if (postFlop) {
      this.postflopOptions.forEach(
        (item) =>
          (item.isDisabled = item.isDisabled && controlItem !== item.value),
      );
    } else {
      this.preflopOptions.forEach(
        (item) =>
          (item.isDisabled = item.isDisabled && controlItem !== item.value),
      );
    }
    if (this.hasLimit) {
      this.preflopOptions[7].isDisabled = true;
      this.postflopOptions[7].isDisabled = true;
    }
    this.postflopOptions[8].isDisabled = false;
  }

  setLimit(value: boolean) {
    this.hasLimit = value;
    this.form = this.hasLimit ? this.formPL : this.formNL;
    if (this.isMobile) {
      this.loadMobileData();
    }

    if (value) {
      this.preflopOptions[7].isDisabled = true;
      this.postflopOptions[7].isDisabled = true;
    }
    this.loadForm();
  }
  addToStorage() {
    this.formNL.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((value) => {
        this.form = this.formNL;
        this.onItemChanges(value);
      });
    this.formPL.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((value) => {
        this.form = this.formPL;
        this.onItemChanges(value);
      });
  }

  onItemChanges(value: any) {
    const postflop = value.postflop;
    const storageKey = this.hasLimit
      ? "sliderShortCutOptionsPL"
      : "sliderShortCutOptionsNL";
    const stored = localStorage.getItem(storageKey);
    const parsedStored = stored ? JSON.parse(stored) : null;

    Object.entries(postflop).forEach(([btn, val]) => {
      if (val === "Enter %") {
        this.savedValue = parsedStored.postflop[btn];
      }
    });
    const customValues = this.hasLimit
      ? this.customPostflopValuesPL
      : this.customPostflopValuesNL;

    Object.keys(customValues).forEach((btnKey) => {
      const currentValue = postflop[btnKey];
      const customValue = customValues[btnKey];

      if (customValue && currentValue !== customValue) {
        delete customValues[btnKey];
      }
    });
    this.filterOptions();
    this.filterOptions(true);
    localStorage.setItem(storageKey, JSON.stringify(value));
    this.postflopOptions[8].isDisabled = false;
    this.gameService.sliderButtonsChanged$.next(true);
  }

  onPostflopSelectChange(event: Event, btn: string): void {
    const selectedValue = (event.target as HTMLSelectElement).value;

    this.filterOptions(true);
    this.setDisabled(btn, true);

    if (selectedValue === "Enter %") {
      const dialogRef = this.dialog.open(EnterCustomAmountDialogComponent, {
        width: "300px",
        data: { street: "postflop", button: btn },
      });
      dialogRef.afterClosed().subscribe((result: string | null) => {
        if (result) {
          if (this.hasLimit) this.customPostflopValuesPL[btn] = result;
          else this.customPostflopValuesNL[btn] = result;
          const postflopGroup = this.form.get("postflop") as FormGroup;
          postflopGroup.get(btn)?.setValue(result);
        } else {
          this.postflopOptions[8].isDisabled = false;
          const postflopGroup = this.form.get("postflop") as FormGroup;
          postflopGroup.get(btn)?.setValue(this.savedValue);
          this.savedValue = "";
        }
      });
    }
  }

  reset() {
    if (this.hasLimit) {
      this.preflopOptions[7].isDisabled = true;
      this.postflopOptions[7].isDisabled = true;
      this.customPostflopValuesPL = {};
      this.formPL.setValue({
        postflop: { ...DEFAULT_SLIDER_SHORTCUT_BUTTONS_PL.postflop },
        preflop: { ...DEFAULT_SLIDER_SHORTCUT_BUTTONS_PL.preflop },
      });
    } else {
      this.preflopOptions[7].isDisabled = false;
      this.postflopOptions[7].isDisabled = false;
      this.customPostflopValuesNL = {};
      this.formNL.setValue({
        postflop: { ...DEFAULT_SLIDER_SHORTCUT_BUTTONS_NL.postflop },
        preflop: { ...DEFAULT_SLIDER_SHORTCUT_BUTTONS_NL.preflop },
      });
    }
  }
}
